import Button, { type ButtonProps, type ButtonSize, type ButtonVariant } from '@ds/components/actions/Button';
import clsx from 'clsx';
import React, { forwardRef, type ReactNode } from 'react';

import styles from './IconButton.module.css';

/**
 * Appended to `label` while `isLoading`. A text `Button` shows its state by changing its visible
 * children ("Saving…"); an icon-only button has no visible label, so its accessible name has to
 * carry the state instead. `aria-busy` alone is not announced on a button. See ADR 0051.
 */
const LOADING_NAME_SUFFIX = ', loading';

/**
 * Visual priority of an icon-only button. `link` is excluded: it renders as inline underlined
 * text sized to surrounding copy, which is meaningless with no label to underline.
 */
export type IconButtonVariant = Exclude<ButtonVariant, 'link'>;

/**
 * Outline of the button's box. Purely a corner-radius choice — the box is a square either way, so
 * a circle is inscribed in the same 40 × 40 / 56 × 56 footprint and nothing about layout changes.
 */
export type IconButtonShape = 'square' | 'circle';

export interface IconButtonProps
    extends Omit<ButtonProps, 'variant' | 'startIcon' | 'endIcon' | 'fullWidth' | 'children' | 'aria-label' | 'danger'> {
    /**
     * The glyph. Pass an icon from `@ds/icons` bare — this component supplies the size clamp (20px at
     * `sm`, 24px at `lg` — the icon/sm and icon/md steps, tracking the same step `size` sets on the box) and `currentColor`, so it
     * needs no wrapper and no `size` prop of its own.
     */
    icon: ReactNode;
    /**
     * The accessible name, applied as `aria-label`. **Required**: the glyph is `aria-hidden`
     * (every `@ds/icons` component is decorative by default), so without this the button has no
     * accessible name at all — a WCAG 4.1.2 failure that is invisible until an axe run happens to
     * cover the screen. Making it non-optional turns that into a compile error instead.
     */
    label: string;
    /**
     * Visual priority of the action.
     * - `primary` — filled, high emphasis.
     * - `secondary` — outlined, medium emphasis. Carries a `--bg-surface` fill as well as the
     *   outline, so an unqualified `IconButton` is a white square; pass `transparent` (inherited
     *   from `Button`) on a coloured surface, or `text` for a borderless affordance.
     * - `text` — ghost, low emphasis.
     * - `elevatedSecondary` — white, `--action-primary` glyph, borderless, seated on a shadow. For
     *   a control that floats *over* other content (Figma "Small FAB"). Tabs' overflow arrows use
     *   this — it is the direct successor of the old single `elevated` variant.
     * - `elevatedPrimary` — same shadow and geometry as `elevatedSecondary`, but a solid
     *   `--action-link` fill with a white glyph.
     *
     * `danger` is omitted from `IconButtonProps` entirely (see below); on `Button`, neither
     * elevated variant supports it either — it is a silent no-op on both, by design.
     * @default 'secondary'
     */
    variant?: IconButtonVariant;
    /**
     * Size scale. `sm` is 40 × 40, `lg` is 56 × 56 — the square counterparts of `Button`'s heights.
     * @default 'sm'
     */
    size?: ButtonSize;
    /**
     * Corner treatment.
     * - `square` — takes the variant's own radius (8px, or 12px for `elevated`).
     * - `circle` — `--radius-full`, overriding the variant. **The footprint is identical**: the box
     *   is already a square, so the circle is inscribed in the same 40 × 40 / 56 × 56 and the focus
     *   ring (a `box-shadow` spread) follows the radius on its own.
     *
     * Reach for `circle` on a borderless, low-emphasis control that sits *inside* other chrome — a
     * dismiss, a close, a field affordance. Keep `square` where the button reads as a button in its
     * own right.
     * @default 'square'
     */
    shape?: IconButtonShape;
    /**
     * Makes the button inert. Rendered as **`aria-disabled`, never the native attribute**, so the
     * button stays focusable and announced — the same choice `Tabs.Tab`, `Menu.Item` and `Select`'s
     * options make. A natively-disabled control that disables itself mid-interaction drops focus to
     * `<body>`; this one does not. Activation is blocked here in JS and by `pointer-events: none`
     * in `Button.module.css`.
     * @default false
     */
    disabled?: boolean;
}

/**
 * A square, icon-only button.
 *
 * It renders a real `<Button>` and overrides nothing but geometry, so every hover, pressed,
 * focus-visible and disabled rule comes from `Button` unchanged and the two can never
 * drift apart. The glyph is passed as `children`, which is what lets it track `Button`'s own
 * per-size `--button-icon-size` (20px `sm` / 24px `lg`; the `sm` step is overridden to `--icon-sm`) instead of the `startIcon`/`endIcon`
 * slots' hard-coded clamp.
 *
 * Use `<Button>` with `startIcon` when there is a label to show. Use this only when there is not.
 *
 * @example
 * <IconButton
 *     icon={<ArrowForwardIosIcon />}
 *     label="Scroll tabs right"
 *     variant="elevated"
 *     onClick={scrollRight}
 * />
 *
 * @example
 * // Circular, for a control sitting inside other chrome. Same 40 × 40 footprint.
 * <IconButton
 *     icon={<CloseIcon />}
 *     label="Close"
 *     variant="text"
 *     shape="circle"
 *     onClick={close}
 * />
 */
const IconButton = forwardRef<HTMLElement, IconButtonProps>(
    (
        {
            icon,
            label,
            variant = 'secondary',
            size = 'sm',
            shape = 'square',
            disabled = false,
            isLoading = false,
            className,
            onClick,
            ...rest
        },
        ref
    ) => (
        <Button
            ref={ref}
            variant={variant}
            size={size}
            isLoading={isLoading}
            // The name is the whole announcement for an icon-only button — there is no visible
            // label to change to "Saving…" the way a text Button's children can — so the loading
            // state is appended to it. `aria-busy` alone is not announced on a button by NVDA, JAWS
            // or VoiceOver.
            aria-label={isLoading ? `${label}${LOADING_NAME_SUFFIX}` : label}
            aria-disabled={disabled || undefined}
            data-icon-only=""
            data-shape={shape}
            className={clsx(styles.iconButton, className)}
            // `aria-disabled` carries no behaviour of its own, so activation has to be blocked
            // explicitly. `pointer-events: none` in Button.module.css closes the pointer path;
            // withholding the handler is what closes Enter and Space, which still reach a focused
            // soft-disabled button. Dropping the handler rather than wrapping it in a guard keeps
            // the union-typed `onClick` from ButtonProps intact.
            onClick={disabled ? undefined : onClick}
            {...rest}
        >
            {/* The glyph travels in Button's label slot, which Button keeps visible while loading
                (a text label should stay readable beside the spinner). An icon-only button has no
                such label, so the spinner must stand in for the glyph rather than sit beside it. */}
            {isLoading ? null : icon}
        </Button>
    )
);

IconButton.displayName = 'IconButton';

export { IconButton };
export default IconButton;
